import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { formatDate, formatHours, formatMoney } from '../../lib/format';
import type { UnpaidBankInvoice } from './api';

/**
 * HOURS IN THE BANK THAT NOBODY HAS PAID FOR YET.
 *
 * Hours on a SENT invoice count in the bank before it is paid (2026-10-06:
 * *"we've never had a client not pay an invoice for hours"*), so they are not
 * overage. Jesse, 2026-10-30: *"I do think we should show a warning if the
 * invoiced hours are still not paid for... but it doesn't need to show as
 * 'overage'."* This is that warning — amber, naming the invoice and what is
 * owed, never red and never the word overage.
 */
export function UnpaidBankHours({
  unpaid,
  className,
}: {
  unpaid: UnpaidBankInvoice[];
  className?: string;
}) {
  if (unpaid.length === 0) return null;
  const hours = unpaid.reduce((sum, u) => sum + u.hours, 0);
  return (
    <div
      className={
        'rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-body-sm text-secondary ' + (className ?? '')
      }
    >
      <p className="flex items-start gap-1.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
        <span>
          <span className="num font-semibold">
            {formatHours(hours, { unit: true })} in this bank {unpaid.length === 1 ? 'is' : 'are'} not paid for yet.
          </span>{' '}
          {unpaid.length === 1 ? 'The invoice was sent, so the hours count' : 'The invoices were sent, so the hours count'}
          {' '}— chase the money, not the hours.
        </span>
      </p>
      <ul className="mt-1.5 space-y-1 pl-5">
        {unpaid.map((u) => {
          const late = daysLate(u.dueDate);
          return (
            <li key={u.invoiceId} className="num">
              <Link to={`/invoices/${u.invoiceId}`} className="font-semibold text-primary hover:underline">
                #{u.invoiceNumber}
              </Link>{' '}
              · {formatHours(u.hours, { unit: true })} · {formatMoney(u.dueAmount)} due {formatDate(u.dueDate)}
              {late > 0 ? <span className="font-semibold text-status-late"> · {late} days late</span> : ''}
              {u.state === 'PARTIAL' ? ' · part paid' : ''}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** "12 h unpaid" — the short form for a list row. */
export function unpaidHoursLabel(unpaid: UnpaidBankInvoice[] | undefined): string | null {
  const hours = (unpaid ?? []).reduce((sum, u) => sum + u.hours, 0);
  return hours > 0 ? `${formatHours(hours, { unit: true })} unpaid` : null;
}

/** Days past a `YYYY-MM-DD` due date, read off the string (never `new Date(day)`). */
function daysLate(due: string): number {
  const [y, m, d] = due.split('-').map(Number);
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((today - Date.UTC(y, m - 1, d)) / 86_400_000);
}
